import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./Header";
import SEO from "./SEO";
import "./FAQ.css";
import Footer from "./Footer";

const faqs = [
  {
    q: `What is LawFusion.AI?`,
    a: `LawFusion.AI is a legal research platform that queries **multiple advanced AI models — including ChatGPT, Claude, Gemini, and Perplexity —** to provide attorneys with a **balanced, side-by-side synthesis** rather than relying on a single AI source.`,
  },
  {
    q: `Why use LawFusion instead of Westlaw or LexisNexis?`,
    a: `Traditional legal databases focus on **case retrieval**, leaving you to manually sift through results and craft the analysis yourself. LawFusion takes a different approach: **five advanced AIs collaborate to research your question, debate both sides, and deliver strategic legal intelligence — not just citations.** For solo and small-firm attorneys, LawFusion provides the analytical power normally reserved for teams of associates, at a fraction of the cost. You get deep, multi-angle insights in minutes, without hours of manual synthesis.`,
  },
  {
    q: `How is LawFusion.AI different from ChatGPT and other AI tools?`,
    a: `ChatGPT gives you one AI's perspective. LawFusion gives you **five advanced AIs collaborating** on your legal question. They challenge each other, debate your arguments, and refine the strongest position. While opposing counsel may rely on a single AI (or none), you show up with a **panel of AI legal analysts** that already stress-tested your reasoning. It's the difference between asking one smart friend for advice versus convening a room full of experts who won't stop until the best answer wins.`,
  },
  {
    q: `What is Debate Mode, and why is it useful for attorneys?`,
    a: `Debate Mode runs your issue through **multiple advanced AIs that argue both sides**, challenge each other's reasoning, and expose weaknesses in real time — similar to a **moot court or adversarial review**. It helps you **identify blind spots, uncover counterarguments, and strengthen your overall case strategy**, giving smaller firms the analytical firepower of a larger team. The final output shows where the AIs agree, disagree, or reveal potential vulnerabilities.`,
  },
  {
    q: `How do I know I can trust the results?`,
    a: `LawFusion shows **where models agree, where they diverge**, and what facts, authority, or risks may still be missing. This supports **better issue-spotting and more informed attorney judgment**—not blind reliance on AI.The models surface gaps and competing arguments; **the attorney remains in control** of the legal strategy and final decisions.`,
  },
  {
    q: `Why can't I just paste my query into multiple AIs myself?`,
    a: `You can — but that's like running your own moot court while also acting as judge. **LawFusion's orchestration algorithm assigns each AI a distinct legal role** (advocate, critic, strategist), structures adversarial debate across multiple rounds, enforces citation‑safe reasoning, and distills areas of agreement and disagreement into a unified strategic analysis. This is far more disciplined and rigorous than pasting the same prompt into separate chat windows.

**Solo attorneys routinely save hours each week that would otherwise be spent manually comparing outputs** — freeing your time for real legal work instead of AI babysitting.`,
  },
  {
    q: `How can I use fewer AI Actions and make my plan last longer?`,
    a: `AI Actions are primarily consumed by document length and the depth of analysis requested. To make your AI Actions go further, **upload only the specific sections you want analyzed**—such as a particular motion, brief section, or argument—and exclude supporting exhibits unless they are essential.

In Research Mode, consider using two or three preferred AI models to compare perspectives instead of running all five. **In Debate Mode, selecting 3 to 5 rounds (rather than 8)** helps keep debates focused while reducing AI Action usage.

Finally, crafting precise, **focused queries upfront reduces the need for follow-up runs**. Limiting unnecessary reruns and reserving deeper analysis for when it truly adds value will significantly extend your monthly AI Action budget without sacrificing output quality.`,
  },
  {
    q: `Can I upload confidential client information?`,
    a: `LawFusion is intended for professional legal use, but attorneys should **exercise professional judgment**. Avoid unnecessary client-identifying details and redact sensitive information when possible. **Do not submit material you are not permitted to share** under your ethical or confidentiality obligations.`,
  },
  {
    q: `How does LawFusion protect my data?`,
    a: `We use **encrypted HTTPS/TLS connections**, **token-based authentication**, and **server-side API key management**. AI responses and prompts are **securely stored** and **never shared publicly**. See our **Privacy Policy** for details.`,
  },
  {
    q: `Is LawFusion compliant with data privacy standards?`,
    a: `Yes. LawFusion follows **recognized U.S. and EU data protection standards**. We **don't store personal client data** or **share user activity** with third parties. Details appear in our **Terms of Service** and **Privacy Policy.**`,
  },
];

const FAQ_NUMBER_EMOJIS = [
  "1️⃣",
  "2️⃣",
  "3️⃣",
  "4️⃣",
  "5️⃣",
  "6️⃣",
  "7️⃣",
  "8️⃣",
  "9️⃣",
];

export default function FAQPage() {
  const { isAuthenticated } = useAuth();
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div>
      <SEO title="FAQ" path="/faq" description="Frequently asked questions about LawFusion — AI-powered legal analysis, pricing, features, and how the platform works." />
      <div className="faq-page">
        <Header />
        <main className="faq-wrapper">
          <section className="faq-card">
            <h1>⚖️ LawFusion.AI — Frequently Asked Questions</h1>

            <div className="faq-list">
              {faqs.map((item, index) => {
                const isOpen = openItems[index];
                return (
                  <div
                    key={item.q}
                    className={`faq-item ${isOpen ? "open" : ""}`}
                    style={{ animationDelay: `${index * 0.08}s` }}
                  >
                    <div className="faq-summary" onClick={() => toggleItem(index)}>
                      <span className="faq-question">
                        <span
                          className={`faq-number${index === 9 ? " faq-number-double" : ""}`}
                          aria-hidden
                        >
                          {index === 9 ? (
                            <>
                              1️⃣0️⃣
                            </>
                          ) : (
                            FAQ_NUMBER_EMOJIS[index]
                          )}
                        </span>
                        {item.q}
                      </span>
                      <span className={`faq-toggle ${isOpen ? "rotated" : ""}`}>
                        ▼
                      </span>
                    </div>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          <p
                            className="faq-answer"
                            dangerouslySetInnerHTML={{
                              __html: item.a.replace(
                                /\*\*(.*?)\*\*/g,
                                "<strong>$1</strong>",
                              ),
                            }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
}
